"use client";

import { components } from "@zapier/api-clients/apis/templates-backend";
import { Divider, Spacer, Text } from "@zapier/design-system";
import { type Template } from "@zapier/templates";
import { Icon, IconName } from "@zapier/zinnia-icons";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { GALLERY_CURATED_LIMIT, GALLERY_FILTERED_LIMIT } from "src/constants";
import { getGalleryTemplates, getTemplates } from "src/lib/actions";
import { useWebMcpCategoryTool } from "src/hooks/useWebMcpCategoryTool";
import { ServiceRoute } from "src/utils/routes";
import { BreadCrumbItem } from "../BreadCrumbs/BreadCrumbs";
import { Sidebar } from "../Sidebar/Sidebar";
import { CustomPageData } from "../TemplateLibraryLanding/TemplateLibraryLanding";
import { Hero } from "./Hero";
import styles from "./TemplateLibraryPage.module.css";
import { TemplatesGrid } from "./TemplatesGrid";
import { Markdown } from "../Markdown";

type Category = components["schemas"]["Category"];

interface TemplateLibraryLandingProps {
  title: string;
  description?: string;
  content?: string;
  image?: string;
  iconName?: IconName;
  crumbs: BreadCrumbItem[];
  templates: Template[];
  showcaseTemplate?: Template;
  categories: Category[];
  productsMenu?: ServiceRoute[];
  customPageData?: CustomPageData; // Optional custom page data
}

export const TemplateLibraryPage = ({
  title,
  description,
  content,
  image,
  iconName,
  crumbs,
  templates: initialTemplates = [],
  showcaseTemplate,
  categories,
  productsMenu,
  customPageData,
}: TemplateLibraryLandingProps) => {
  // Determine if this is a custom category page (has navigation data)
  const isCustomPage = !!customPageData;
  const searchParams = useSearchParams();
  const params = useParams();
  const pathname = usePathname();
  const pageSlug = params?.slug;
  const t = useTranslations("TemplateLibrary");

  const filterCategory = searchParams?.get("category");
  const filterApp = searchParams?.get("app");
  const hasNavigationFilters = filterCategory || filterApp;

  // AGD-28: expose the sidebar category filter as a WebMCP tool, matching how
  // this page's own category links navigate (in-place query filter on custom
  // pages, /templates/{slug} otherwise). No-op without WebMCP support.
  const toolCategories =
    isCustomPage && customPageData
      ? customPageData.navigationCategories
      : categories;
  useWebMcpCategoryTool(
    toolCategories,
    isCustomPage ? (slug) => `${pathname}?category=${slug}` : undefined,
  );

  const [templates, setTemplates] = useState<Template[]>(
    hasNavigationFilters ? [] : initialTemplates,
  );

  useEffect(() => {
    if (isCustomPage) {
      const updateFilteredTemplates = async () => {
        const galleryTemplatesResponse = await getGalleryTemplates({
          draftMode: customPageData.isDraftModeEnabled,
          sourceCategories: customPageData.sourceCategories,
          navigationCategories: filterCategory ? [filterCategory] : undefined,
          navigationApp: filterApp ? filterApp : undefined,
          limit: hasNavigationFilters
            ? GALLERY_FILTERED_LIMIT
            : GALLERY_CURATED_LIMIT,
        });

        setTemplates(galleryTemplatesResponse.templates);
      };

      updateFilteredTemplates();
    } else if (filterApp && pageSlug) {
      const updateFilteredTemplates = async () => {
        const updatedTemplates = await getTemplates({
          apps: [filterApp],
          products: [pageSlug as string],
          limit: GALLERY_FILTERED_LIMIT,
        });

        setTemplates(updatedTemplates.templates);
      };

      updateFilteredTemplates();
    }
  }, [filterCategory, filterApp, isCustomPage, pageSlug]);

  return (
    <>
      <Hero
        title={title}
        description={description}
        crumbs={crumbs}
        image={image}
        iconName={iconName}
      />
      <div className={styles.container}>
        <div className={styles.mobileMenuContainer}>
          <details className={styles.mobileMenu}>
            <summary className={styles.mobileMenuToggle}>
              <Text type="SmallPrint2">{t("menu")}</Text>
              <div className={styles.mobileMenuIcon}>
                <Icon name="arrowSmallDown" size={20} isBlock />
              </div>
            </summary>
            <Sidebar
              categories={
                isCustomPage && customPageData
                  ? customPageData.navigationCategories
                  : categories
              }
              navigationApps={customPageData?.navigationApps || []}
              productsMenu={isCustomPage ? [] : productsMenu}
              includeHomepage
              isCustomPage={isCustomPage}
            />
            <Spacer height={12} />
            <Divider height={1} />
            <Spacer height={20} />
          </details>
        </div>
        <div className={styles.sidebarContainer}>
          {
            <Sidebar
              categories={
                isCustomPage && customPageData
                  ? customPageData.navigationCategories
                  : categories
              }
              navigationApps={customPageData?.navigationApps || []}
              productsMenu={isCustomPage ? [] : productsMenu}
              includeHomepage={isCustomPage}
              isCustomPage={isCustomPage}
            />
          }
        </div>
        <div>
          {content && (
            <div className={styles.content}>
              <Markdown content={content} />
            </div>
          )}
          <TemplatesGrid
            title={title}
            templates={templates}
            showcaseTemplate={showcaseTemplate}
          />
        </div>
      </div>
    </>
  );
};
